import { ShieldCheck, ShieldAlert, Info } from 'lucide-react'
import { ProductWarranty } from '@/lib/verticalUI'

export interface ProductWarrantyFieldGroupProps {
  value?: ProductWarranty | null
  onChange: (value: ProductWarranty) => void
  disabled?: boolean
}

export const ProductWarrantyFieldGroup: React.FC<ProductWarrantyFieldGroupProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const current: ProductWarranty = {
    hasWarranty: value?.hasWarranty ?? false,
    warrantyType: value?.warrantyType ?? (value?.hasWarranty ? 'tienda' : 'sin_garantia'),
    durationValue: value?.durationValue ?? undefined,
    durationUnit: value?.durationUnit ?? 'meses',
    conditions: value?.conditions ?? '',
    noWarrantyReason: value?.noWarrantyReason ?? '',
  }

  const update = (patch: Partial<ProductWarranty>) => {
    onChange({
      ...current,
      ...patch,
    })
  }

  const handleToggleWarranty = (hasWarranty: boolean) => {
    if (hasWarranty) {
      update({
        hasWarranty: true,
        warrantyType: current.warrantyType === 'sin_garantia' ? 'tienda' : (current.warrantyType || 'tienda'),
        durationValue: current.durationValue || 1,
        durationUnit: current.durationUnit || 'meses',
        noWarrantyReason: undefined,
      })
    } else {
      update({
        hasWarranty: false,
        warrantyType: 'sin_garantia',
        durationValue: undefined,
        durationUnit: undefined,
        conditions: undefined,
      })
    }
  }

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white p-4 space-y-4 shadow-2xs">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-emerald-50 text-[#075E54] flex items-center justify-center border border-emerald-200/60">
            {current.hasWarranty ? (
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            ) : (
              <ShieldAlert className="w-4 h-4 text-slate-400" />
            )}
          </div>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Garantía del Producto
            </h4>
            <p className="text-[11px] text-slate-400">
              Política específica consultada por el asistente de WhatsApp
            </p>
          </div>
        </div>

        <div className="flex items-center rounded-xl bg-slate-100 p-0.5 border border-slate-200">
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleToggleWarranty(true)}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              current.hasWarranty
                ? 'bg-emerald-600 text-white shadow-2xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Con Garantía
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleToggleWarranty(false)}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
              !current.hasWarranty
                ? 'bg-slate-700 text-white shadow-2xs'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Sin Garantía
          </button>
        </div>
      </div>

      {/* Body: Con Garantía */}
      {current.hasWarranty ? (
        <div className="space-y-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Tipo de Garantía */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                Tipo de Garantía *
              </label>
              <select
                disabled={disabled}
                value={current.warrantyType || 'tienda'}
                onChange={(e) => update({ warrantyType: e.target.value as any })}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none transition"
              >
                <option value="tienda">Garantía de la Tienda / Local</option>
                <option value="fabricante">Garantía del Fabricante / Marca</option>
                <option value="distribuidor">Garantía de Distribuidor</option>
                <option value="taller">Garantía de Taller</option>
              </select>
            </div>

            {/* Duración: Valor y Unidad */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                Tiempo de Cobertura *
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  max="3650"
                  disabled={disabled}
                  value={current.durationValue ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : undefined
                    update({ durationValue: val })
                  }}
                  placeholder="Ej: 30"
                  className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none font-semibold transition"
                />
                <select
                  disabled={disabled}
                  value={current.durationUnit || 'meses'}
                  onChange={(e) => update({ durationUnit: e.target.value as any })}
                  className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none transition"
                >
                  <option value="horas">Horas (ej. repuesto eléctrico 24h/48h)</option>
                  <option value="dias">Días</option>
                  <option value="meses">Meses</option>
                  <option value="anos">Años</option>
                </select>
              </div>
            </div>
          </div>

          {/* Condiciones de Cobertura */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
              Condiciones o Requisitos de Garantía
            </label>
            <input
              type="text"
              disabled={disabled}
              maxLength={300}
              value={current.conditions || ''}
              onChange={(e) => update({ conditions: e.target.value })}
              placeholder="Ej: Cubre defectos de fábrica. Debe conservar caja original o comprobante de compra."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] bg-white outline-none transition"
            />
          </div>
        </div>
      ) : (
        /* Body: Sin Garantía */
        <div className="space-y-2 bg-amber-50/50 border border-amber-200/70 rounded-xl p-3">
          <label className="block text-xs font-bold uppercase tracking-wider text-amber-800 mb-1">
            Motivo o Aclaratoria (opcional)
          </label>
          <input
            type="text"
            disabled={disabled}
            maxLength={200}
            value={current.noWarrantyReason || ''}
            onChange={(e) => update({ noWarrantyReason: e.target.value })}
            placeholder="Ej: Producto eléctrico probado antes de entregar, sin cambio ni devolución."
            className="w-full rounded-xl border border-amber-200/80 px-3 py-2 text-xs focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 bg-white outline-none transition"
          />
          <p className="text-[11px] text-amber-700/80">
            Si el cliente pregunta por garantía para este artículo, el bot le explicará con total honestidad este motivo.
          </p>
        </div>
      )}

      {/* Footer Info Note */}
      <div className="flex items-start gap-1.5 pt-1 text-[11px] text-slate-400">
        <Info className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
        <span>
          El bot de ventas responderá las preguntas de garantía basándose exactamente en esta configuración, evitando inventar garantías o remitir a políticas genéricas desactualizadas.
        </span>
      </div>
    </div>
  )
}

export default ProductWarrantyFieldGroup
